import React, { useMemo } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import type { EventPageType } from '@graphql/generated-base';
import { BrannanNavigation } from '@graphql/generated-base';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { Box } from '@withjoy/joykit';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { isHeaderLayoutWithJsonContainerId } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { isHeaderLayoutWithPhotos } from '@shared/utils/websiteDesign';
import { useTranslation } from '@shared/core';
import { useLayout } from '@shared/context/LayoutProvider';
import { useEventWebsiteGuestData } from '@apps/guest/shared/hooks/useEventWebsiteGuestData';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useEventInfo } from '@shared/utils/eventInfo';

const FULL_WIDTH_LAYOUTS: PhotoPresentationLayout[] = ['fullWidth', 'photoSlider', 'photoTrio', 'photoDuoLeft', 'photoDuoRight', 'photoMarquee', 'videoArch'];

export interface HeaderPhotoV2Props {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageSlug?: string;
  pageType: Maybe<EventPageType>;
  showPlaceholder?: boolean;
  children: React.ReactNode;
}

export const HeaderPhoto = (props: HeaderPhotoV2Props) => {
  const { t } = useTranslation('joykit');
  const tInlineEditor = t('inlineEditor');
  const { children, websiteHeader, pageSlug, pageType, showPlaceholder = false } = props;
  const { brannanNavigation } = useLayout();

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  };

  const handleEditMonogramClick = () => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editMonogram' },
    });
  };

  const isMonogram = websiteHeader.headerPhoto?.presentationLayout === 'monogram';
  const isVideoArch = websiteHeader.headerPhoto?.presentationLayout === 'videoArch';

  const elementLabel = useMemo(() => {
    if (isMonogram) {
      return tInlineEditor.type.monogram();
    }
    return tInlineEditor.type.photo();
  }, [isMonogram, tInlineEditor.type]);

  const hasContent = useMemo(() => {
    if (isHeaderLayoutWithPhotos(websiteHeader.headerPhoto)) {
      return websiteHeader.headerPhoto.photos.length > 0;
    }

    if (isHeaderLayoutWithJsonContainerId(websiteHeader.headerPhoto)) {
      return !!websiteHeader.headerPhoto.jsonContainerId;
    }

    return false;
  }, [websiteHeader.headerPhoto]);
  const { eventHandle } = useEventInfo();
  const { data, loading } = useEventWebsiteGuestData({ eventHandle: eventHandle || '' });
  const pages = data?.eventByName?.pages;
  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  const hasTopPadding = useMemo(() => {
    if (isVideoArch) {
      // For videoArch the padding is handled by the VideoArch component
      return false;
    }
    return hasContent || showPlaceholder;
  }, [hasContent, showPlaceholder, isVideoArch]);

  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage } = useMemo(() => checkIsWelcomePage(pageSlug, pageType), [checkIsWelcomePage, pageSlug, pageType]);

  if (isWelcomePage || (!loading && pageIndex === 0) || brannanNavigation === BrannanNavigation.singlePage) {
    return (
      <Box paddingTop={hasTopPadding ? { _: 0, sm4: 9 } : 0}>
        <InlineEditor
          elementLabel={elementLabel}
          wrapperType="actionInside"
          actionData={
            isMonogram
              ? {
                  editMonogram: handleEditMonogramClick,
                }
              : {
                  editPhoto: handleEditPhotoClick,
                }
          }
          componentName="headerPhoto"
          pageName={pageType}
          pageSlug={pageSlug || ''}
          isEligibleForInlineEditing={!isVideoArch}
          wrapperCSS={FULL_WIDTH_LAYOUTS.includes(websiteHeader.headerPhoto?.presentationLayout) ? { width: '100vw', left: 'calc(-50vw + 50%)' } : {}}
        >
          {children}
        </InlineEditor>
      </Box>
    );
  }

  return null;
};
